import { Fragment, useEffect, useMemo, useState, type ReactElement } from 'react';
import { toast } from 'react-toastify';
import { AddressReadOnly } from '../../AddressFieldsGroup';
import type { PurchaseOrder, UpdatePurchaseOrderPayload } from '../../domain/types';
import {
  formatMoney,
  formatPoStatus,
  parseNonNegativeUnitPrice,
  parseOptionalUsdAmount,
  purchaseOrderGrandTotal,
  purchaseOrderLineItemsSubtotal
} from '../../domain/formatters';

interface PurchaseOrderDetailSidePanelProps {
  po: PurchaseOrder;
  onClose: () => void;
  onExportPdf: (po: PurchaseOrder) => void;
  onUpdatePurchaseOrder?: (id: string, payload: UpdatePurchaseOrderPayload) => void | Promise<void>;
  onMarkPaid: (id: string) => void;
  /** Procurement staff: enqueue PO PDF to vendor email (Azure queue). */
  onEmailVendor?: (po: PurchaseOrder) => void | Promise<void>;
  variant?: 'panel' | 'page' | 'embedded';
  /** Shown only when variant is embedded (e.g. RFQ page after PO is created). */
  onOpenPoFullPage?: (poId: string) => void;
  onBack?: () => void;
  readOnly?: boolean;
}

export function PurchaseOrderDetailSidePanel({
  po,
  onClose,
  onExportPdf,
  onUpdatePurchaseOrder,
  onMarkPaid,
  onEmailVendor,
  variant = 'panel',
  onOpenPoFullPage,
  onBack,
  readOnly = false
}: PurchaseOrderDetailSidePanelProps): ReactElement {
  const isPage = variant === 'page';
  const isEmbedded = variant === 'embedded';

  const [feeFreight, setFeeFreight] = useState('');
  const [feeMisc, setFeeMisc] = useState('');
  const [lineUnitInputs, setLineUnitInputs] = useState<string[]>([]);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingEditing, setPricingEditing] = useState(false);

  const lineSnapshot = useMemo(
    () => po.lineItems.map((l) => `${l.unitCost}:${l.quantity}:${l.lineTotal}`).join(';'),
    [po.lineItems]
  );

  useEffect(() => {
    setPricingEditing(false);
  }, [po.id]);

  function syncPricingFromPo(p: PurchaseOrder): void {
    const f = p.freightAmount ?? 0;
    const m = p.miscellaneousFeeAmount ?? 0;
    setFeeFreight(f !== 0 ? String(f) : '');
    setFeeMisc(m !== 0 ? String(m) : '');
    setLineUnitInputs(p.lineItems.map((l) => String(l.unitCost)));
  }

  useEffect(() => {
    if (pricingEditing) {
      return;
    }
    syncPricingFromPo(po);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when po id/fees/lines change; not while editing
  }, [po.id, po.freightAmount, po.miscellaneousFeeAmount, lineSnapshot, pricingEditing]);

  function beginEditPricing(): void {
    syncPricingFromPo(po);
    setPricingEditing(true);
  }

  function cancelEditPricing(): void {
    syncPricingFromPo(po);
    setPricingEditing(false);
  }

  function setLineUnitAt(index: number, value: string): void {
    setLineUnitInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  async function savePricing(): Promise<void> {
    if (!onUpdatePurchaseOrder) {
      return;
    }
    const fp = parseOptionalUsdAmount(feeFreight);
    const mp = parseOptionalUsdAmount(feeMisc);
    if (!fp.ok || !mp.ok) {
      let errMsg = 'Invalid amount.';
      if (!fp.ok) {
        errMsg = fp.message;
      } else if (!mp.ok) {
        errMsg = mp.message;
      }
      toast.error(errMsg);
      return;
    }

    const unitCosts: number[] = [];
    for (let i = 0; i < po.lineItems.length; i++) {
      const raw = lineUnitInputs[i] ?? '';
      const up = parseNonNegativeUnitPrice(raw);
      if (!up.ok) {
        toast.error(
          po.lineItems.length > 1
            ? `Line ${i + 1}: ${up.message}`
            : up.message
        );
        return;
      }
      unitCosts.push(up.amount);
    }

    const payload: UpdatePurchaseOrderPayload = {
      freightAmount: fp.amount,
      miscellaneousFeeAmount: mp.amount,
      lineItemUnitCosts: unitCosts
    };

    setPricingSaving(true);
    try {
      await onUpdatePurchaseOrder(po.id, payload);
      setPricingEditing(false);
    } finally {
      setPricingSaving(false);
    }
  }

  const headerAndBody = (
    <>
      <div className="side-panel-header">
        <div className="side-panel-title-row">
          {isPage && onBack && (
            <button type="button" className="secondary panel-back-btn" onClick={onBack}>
              ← Back to list
            </button>
          )}
          <h3>PO {po.purchaseOrderNumber}</h3>
        </div>
        <div className="side-panel-actions">
          <button type="button" className="secondary" onClick={() => onExportPdf(po)}>
            Export PDF
          </button>
          {onEmailVendor && (
            <button type="button" className="secondary" onClick={() => void onEmailVendor(po)}>
              Email Vendor
            </button>
          )}
          {isEmbedded && onOpenPoFullPage ? (
            <button type="button" className="secondary" onClick={() => onOpenPoFullPage(po.id)}>
              Open full page
            </button>
          ) : null}
          {!isPage && !isEmbedded && (
            <button type="button" className="icon-close" onClick={onClose} aria-label="Close panel">
              ×
            </button>
          )}
        </div>
      </div>
      <div className="side-panel-body">
        <dl className="detail-list">
          <dt>PO number</dt>
          <dd>{po.purchaseOrderNumber}</dd>
          <dt>Status</dt>
          <dd>
            <span className={`status-pill po-status-${po.status}`}>{formatPoStatus(po.status)}</span>
          </dd>
          <dt>Request number</dt>
          <dd>{po.requestNumber}</dd>
          <dt>Well</dt>
          <dd>
            {po.wellInfo.wellName?.trim() || '—'}
            {po.wellInfo.field?.trim() ? ` · ${po.wellInfo.field.trim()}` : ''}
          </dd>
          {po.wellInfo.wellId?.trim() ? (
            <>
              <dt>Well ID</dt>
              <dd className="mono-id">{po.wellInfo.wellId.trim()}</dd>
            </>
          ) : null}
          <dt>Linked request ID</dt>
          <dd className="mono-id">{po.procurementRequestId}</dd>
          <dt>Supplier</dt>
          <dd>{po.vendorName}</dd>
          <dt>Supplier address</dt>
          <dd>
            <AddressReadOnly value={po.vendorAddress} />
          </dd>
          <dt>Ship to</dt>
          <dd>
            <AddressReadOnly value={po.deliveryAddress} />
          </dd>
          <dt>Incoterms</dt>
          <dd>{po.incoterms}</dd>
          <dt>Created by</dt>
          <dd>{po.createdByName}</dd>
          <dt>Created</dt>
          <dd>{new Date(po.createdAtUtc).toLocaleString()}</dd>
          {po.paidAtUtc && (
            <>
              <dt>Paid at</dt>
              <dd>{new Date(po.paidAtUtc).toLocaleString()}</dd>
            </>
          )}
          <dt>Freight</dt>
          <dd>{formatMoney(po.freightAmount ?? 0)}</dd>
          <dt>Miscellaneous fee</dt>
          <dd>{formatMoney(po.miscellaneousFeeAmount ?? 0)}</dd>
          <dt>Lines subtotal</dt>
          <dd>{formatMoney(purchaseOrderLineItemsSubtotal(po))}</dd>
          <dt>Order total</dt>
          <dd>{formatMoney(purchaseOrderGrandTotal(po))}</dd>
        </dl>
        <table className="po-lines">
          <thead>
            <tr>
              <th>Item</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit price</th>
              <th>Line total</th>
            </tr>
          </thead>
          <tbody>
            {po.lineItems.map((line, idx) => (
              <tr key={`${line.partNumber}-${idx}`}>
                <td>{line.partNumber}</td>
                <td>{line.description?.trim() || line.equipmentType}</td>
                <td>{line.quantity}</td>
                <td>{formatMoney(line.unitCost)}</td>
                <td>{formatMoney(line.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {po.status === 'created' && !readOnly && onUpdatePurchaseOrder && !pricingEditing && (
          <div className="panel-actions full-width">
            <h4 className="full-width">Pricing &amp; fees</h4>
            <p className="hint full-width">
              Freight {formatMoney(po.freightAmount ?? 0)} · Miscellaneous {formatMoney(po.miscellaneousFeeAmount ?? 0)}
            </p>
            <p className="hint full-width">
              {po.lineItems.map((line, idx) => (
                <Fragment key={`${line.partNumber}-${idx}`}>
                  {idx > 0 ? ' · ' : null}
                  {line.partNumber}: {formatMoney(line.unitCost)} × {line.quantity}
                </Fragment>
              ))}
            </p>
            <div className="row">
              <button type="button" className="secondary" onClick={beginEditPricing}>
                Edit
              </button>
            </div>
          </div>
        )}
        {po.status === 'created' && !readOnly && onUpdatePurchaseOrder && pricingEditing && (
          <div className="panel-actions full-width po-fees-edit">
            <h4 className="full-width">Edit unit prices, freight &amp; fees</h4>
            <p className="hint full-width">
              Update line unit prices (line totals recalculate from quantity). Freight and miscellaneous fee are optional
              USD amounts. Click Save when finished.
            </p>
            {po.lineItems.map((line, idx) => {
              const parsed = parseNonNegativeUnitPrice(lineUnitInputs[idx] ?? '');
              const unit = parsed.ok ? parsed.amount : 0;
              const previewTotal = line.quantity * unit;
              return (
                <div key={`${line.partNumber}-edit-${idx}`} className="full-width po-line-price-edit">
                  <strong className="po-line-price-edit__label">
                    {line.partNumber} · Qty {line.quantity}
                  </strong>
                  <label className="full-width">
                    Unit price (USD)
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lineUnitInputs[idx] ?? ''}
                      onChange={(e) => setLineUnitAt(idx, e.target.value)}
                    />
                  </label>
                  <p className="hint">Line total: {formatMoney(previewTotal)}</p>
                </div>
              );
            })}
            <label className="full-width">
              Freight (USD)
              <input
                type="number"
                min={0}
                step="0.01"
                value={feeFreight}
                onChange={(e) => setFeeFreight(e.target.value)}
                placeholder="0"
              />
            </label>
            <label className="full-width">
              Miscellaneous fee (USD)
              <input
                type="number"
                min={0}
                step="0.01"
                value={feeMisc}
                onChange={(e) => setFeeMisc(e.target.value)}
                placeholder="0"
              />
            </label>
            <div className="row">
              <button type="button" disabled={pricingSaving} onClick={() => void savePricing()}>
                {pricingSaving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="secondary" disabled={pricingSaving} onClick={cancelEditPricing}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {po.status === 'created' && !readOnly && (
          <div className="panel-actions">
            <button type="button" onClick={() => void onMarkPaid(po.id)}>
              Mark paid
            </button>
          </div>
        )}
      </div>
    </>
  );

  if (isEmbedded) {
    return (
      <section className="nested-card po-detail-embedded" aria-label="Purchase order details">
        {headerAndBody}
      </section>
    );
  }

  return (
    <aside className={`side-panel${isPage ? ' side-panel--page' : ''}`} aria-label="Purchase order details">
      {headerAndBody}
    </aside>
  );
}
